/**
 * A batch sender for exposures and events: one POST per batch of at most 50, sent after `delayMs` or at once when
 * 20 are waiting, never blocking the page. Every item carries its own id, so a retried batch is stored once.
 *
 * - 2xx: done. 422: malformed, dropped (retrying cannot help).
 * - 429: wait Retry-After (seconds, at most 60), then one more try.
 * - 5xx or a network error: retried after 1 s, 4 s and 10 s, then dropped.
 */
export const MAX_BATCH = 50;
export const SEND_AT = 20;
const RETRY_DELAYS_MS = [1_000, 4_000, 10_000];
export class SendQueue {
    options;
    items = [];
    timer = null;
    sending = null;
    constructor(options) {
        this.options = options;
    }
    get size() {
        return this.items.length;
    }
    push(item) {
        this.items.push(item);
        if (this.items.length >= SEND_AT) {
            void this.flush();
        }
        else if (this.timer == null) {
            this.timer = setTimeout(() => void this.flush(), this.options.delayMs);
        }
    }
    /** Sends everything queued; resolves when it is sent or given up on. Never rejects. */
    async flush() {
        if (this.timer != null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        if (this.sending)
            await this.sending;
        while (this.items.length) {
            const batch = this.items.splice(0, MAX_BATCH);
            this.sending = this.send(batch);
            await this.sending;
            this.sending = null;
        }
    }
    async send(batch) {
        const body = JSON.stringify(this.options.body(batch));
        let rateLimitedOnce = false;
        for (let attempt = 0;; attempt++) {
            let status = 0;
            let retryAfter = 0;
            try {
                const response = await this.options.fetch(this.options.url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                    keepalive: true,
                });
                status = response.status;
                retryAfter = Number(response.headers.get('Retry-After') ?? 0);
            }
            catch {
                status = 0; // the network
            }
            if (status >= 200 && status < 300)
                return;
            if (status === 422 || (status >= 400 && status < 500 && status !== 429)) {
                this.options.onDrop?.(`rejected (${status})`, batch.length);
                return;
            }
            if (status === 429) {
                if (rateLimitedOnce)
                    break;
                rateLimitedOnce = true;
                await this.options.sleep(Math.min(Number.isFinite(retryAfter) ? retryAfter : 1, 60) * 1_000);
                continue;
            }
            const delay = RETRY_DELAYS_MS[attempt];
            if (delay == null)
                break;
            await this.options.sleep(delay);
        }
        this.options.onDrop?.('gave up after retries', batch.length);
    }
}
